<!-- Hero -->
<div class="dashboard-hero">
  <div class="hero-content">
    <h1 class="hero-title">
      <i class="bi bi-diagram-3 hero-icon"></i> Systems Management
    </h1>
    <p class="hero-subtitle">Manage and configure system definitions for BPSolver.</p>
  </div>
</div>

<div class="container dashboard-container">

  <!-- Error -->
  <div class="alert alert-warning alert-dismissible" role="alert" *ngIf="displayError">
    <i class="bi bi-exclamation-triangle-fill me-2"></i>{{ errorMessage }}
  </div>

  <div class="modern-card mb-4">
    <div class="modern-card-header">
      <div class="card-title-wrapper">
        <i class="bi bi-diagram-3 card-icon"></i>
        <h5 class="modern-card-title">Systems</h5>
      </div>
      <div class="header-actions">
        <button type="button" class="btn-modern-action btn-action-primary"
          (click)="openCreateSystemModal(createSystemModal)">
          <i class="bi bi-plus-lg"></i> Create System
        </button>
      </div>
    </div>

    <div class="modern-card-body">
      <form class="article-search-wrapper">

        <!-- Search + Filter row -->
        <div class="search-filter-row">
          <div class="search-field-wrapper">
            <label for="table-filtering-search" class="article-search-label">
              <i class="bi bi-search"></i> Full text search:
            </label>
            <input id="table-filtering-search" placeholder="Search systems"
              class="form-control search-input" type="text" name="searchTerm"
              [(ngModel)]="searchTerm"
              (keydown.enter)="search($event)"
              (input)="applyFilters()" />
          </div>

          <div class="filter-field-wrapper">
            <label class="article-search-label"><i class="bi bi-funnel-fill"></i> Filter:</label>
            <div class="filter-controls">
              <span ngbDropdown (keydown.enter)="$event.preventDefault()">
                <button class="btn btn-filter-modern" ngbDropdownToggle
                  (keydown.enter)="$event.preventDefault()">
                  <i class="bi bi-funnel-fill"></i> Filter
                </button>
                <ul ngbDropdownMenu (keydown.enter)="$event.preventDefault()">
                  <li class="dropdown-item" *ngFor="let filter of filterOptions">
                    <input type="checkbox" [checked]="filter.selected"
                      (change)="onFilterChange(filter, $event)" />
                    {{ filter.label }}
                  </li>
                </ul>
              </span>
              <button type="button" class="btn-modern-action btn-action-secondary"
                (click)="clearAllFilters()">
                <i class="bi bi-x-circle"></i> Clear Filters
              </button>
              <span class="d-flex flex-wrap gap-1">
                <span class="filter-tag" *ngFor="let filter of getSelectedFilters()">
                  {{ filter.label }}
                  <span (click)="removeFilter(filter)" aria-label="Close" class="filter-tag-close">&#x2715;</span>
                </span>
              </span>
            </div>
          </div>
        </div>

        <div *ngIf="loading" class="loader-overlay">
          <div class="spinner-border text-primary"></div>
        </div>

        <!-- Systems table -->
        <div class="mgmt-table-scroll-wrapper mt-3" *ngIf="filteredSystems.length">
          <table class="mgmt-table">
            <thead>
              <tr>
                <th scope="col" (click)="onSort('name')">
                  System Name <i class="bi ms-1" [ngClass]="getSortIcon('name')"></i>
                </th>
                <th scope="col" (click)="onSort('material')">
                  Material <i class="bi ms-1" [ngClass]="getSortIcon('material')"></i>
                </th>
                <th scope="col" (click)="onSort('type')">
                  Type <i class="bi ms-1" [ngClass]="getSortIcon('type')"></i>
                </th>
                <th scope="col">Regions</th>
                <th scope="col">Articles</th>
                <th scope="col" (click)="onSort('createdAt')">
                  Created At <i class="bi ms-1" [ngClass]="getSortIcon('createdAt')"></i>
                </th>
                <th scope="col" (click)="onSort('updatedAt')">
                  Updated At <i class="bi ms-1" [ngClass]="getSortIcon('updatedAt')"></i>
                </th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let system of filteredSystems">
                <td>{{ system.name }}</td>
                <td>{{ system.material }}</td>
                <td>{{ system.type }}</td>
                <td>
                  <span *ngFor="let region of system.regions; let isLast = last">
                    <span class="system-badge">{{ region.country }}</span><span *ngIf="!isLast"> </span>
                  </span>
                </td>
                <td>
                  <span class="system-badge">{{ getArticleCount(system.id || '') }}</span>
                </td>
                <td>{{ formatDate(system.createdAt) }}</td>
                <td>{{ formatDate(system.updatedAt) }}</td>
                <td>
                  <div class="row-actions">
                    <button type="button" class="btn-modern-action btn-action-primary"
                      (click)="openSystemDetailModal(systemDetailModal, system)">
                      <i class="bi bi-eye"></i> View
                    </button>
                    <button type="button" class="btn-modern-action btn-action-secondary"
                      (click)="openEditSystemModal(editSystemModal, system)">
                      <i class="bi bi-pencil"></i> Edit
                    </button>
                    <button type="button" class="btn-icon-danger"
                      (click)="deleteSystem(system)">
                      <i class="bi bi-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div *ngIf="!filteredSystems.length && !loading" class="mgmt-empty-state">
          <i class="bi bi-inbox"></i>
          <p>No systems found.</p>
        </div>

        <!-- Pagination info -->
        <div *ngIf="totalItems > 0" class="pagination-info">
          <span>Showing {{ getItemRangeStart() }}–{{ getItemRangeEnd() }} of {{ totalItems }} systems</span>
          <span>Page {{ currentPage }} of {{ getTotalPages() }}</span>
        </div>

        <!-- Pagination -->
        <nav *ngIf="totalItems > itemsPerPage" aria-label="Systems pagination" class="mgmt-pagination">
          <ul class="pagination justify-content-center">
            <li class="page-item" [class.disabled]="currentPage === 1">
              <a class="page-link" (click)="currentPage !== 1 ? onPageChange(currentPage - 1, $event) : null"
                href="#" role="button"
                [style.pointer-events]="currentPage === 1 ? 'none' : 'auto'">Previous</a>
            </li>
            <li class="page-item" *ngIf="getPagesArray()[0] > 1">
              <a class="page-link" (click)="onPageChange(1, $event)" href="#" role="button">1</a>
            </li>
            <li class="page-item disabled" *ngIf="getPagesArray()[0] > 2">
              <span class="page-link">...</span>
            </li>
            <li class="page-item" *ngFor="let page of getPagesArray()" [class.active]="currentPage === page">
              <a class="page-link" (click)="onPageChange(page, $event)" href="#" role="button">{{ page }}</a>
            </li>
            <li class="page-item disabled"
              *ngIf="getPagesArray()[getPagesArray().length - 1] < getTotalPages() - 1">
              <span class="page-link">...</span>
            </li>
            <li class="page-item"
              *ngIf="getPagesArray()[getPagesArray().length - 1] < getTotalPages()">
              <a class="page-link" (click)="onPageChange(getTotalPages(), $event)" href="#" role="button">{{ getTotalPages() }}</a>
            </li>
            <li class="page-item" [class.disabled]="currentPage === getTotalPages()">
              <a class="page-link"
                (click)="currentPage !== getTotalPages() ? onPageChange(currentPage + 1, $event) : null"
                href="#" role="button"
                [style.pointer-events]="currentPage === getTotalPages() ? 'none' : 'auto'">Next</a>
            </li>
          </ul>
        </nav>

      </form>
    </div>
  </div>
</div>

<!-- System Detail Modal -->
<ng-template #systemDetailModal let-modal>
  <div class="modal-header detail-modal-header">
    <div class="detail-modal-title-wrapper">
      <div class="detail-modal-icon">
        <i class="bi bi-diagram-3"></i>
      </div>
      <div>
        <h4 class="modal-title detail-modal-title" id="modal-basic-title">{{ modalContent?.name }}</h4>
        <p class="detail-modal-subtitle">System Details</p>
      </div>
    </div>
    <button type="button" class="btn-close btn-close-white" aria-label="Close"
      (click)="modal.dismiss('Cross click')"></button>
  </div>
  <div class="modal-body detail-modal-body">
    <div class="detail-info-grid">
      <div class="detail-info-row">
        <div class="detail-info-label"><i class="bi bi-tag-fill me-2"></i>Name</div>
        <div class="detail-info-value">{{ modalContent?.name }}</div>
      </div>
      <div class="detail-info-row">
        <div class="detail-info-label"><i class="bi bi-gem me-2"></i>Material</div>
        <div class="detail-info-value">{{ modalContent?.material || '&mdash;' }}</div>
      </div>
      <div class="detail-info-row">
        <div class="detail-info-label"><i class="bi bi-layers-fill me-2"></i>Type</div>
        <div class="detail-info-value">{{ modalContent?.type || '&mdash;' }}</div>
      </div>
      <div class="detail-info-row">
        <div class="detail-info-label"><i class="bi bi-calendar-plus-fill me-2"></i>Created</div>
        <div class="detail-info-value">{{ formatDate(modalContent?.createdAt) || '&mdash;' }}</div>
      </div>
      <div class="detail-info-row">
        <div class="detail-info-label"><i class="bi bi-calendar-check-fill me-2"></i>Updated</div>
        <div class="detail-info-value">{{ formatDate(modalContent?.updatedAt) || '&mdash;' }}</div>
      </div>
    </div>

    <div *ngIf="modalContent?.regions?.length" class="mt-4">
      <div class="detail-section-title"><i class="bi bi-geo-alt-fill me-2"></i>Regions</div>
      <div class="mgmt-table-scroll-wrapper">
        <table class="mgmt-table">
          <thead>
            <tr>
              <th>Country</th>
              <th>City</th>
              <th>State</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let region of modalContent?.regions">
              <td>{{ region.country }}</td>
              <td>{{ region.city }}</td>
              <td>{{ region.state }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
  <div class="modal-footer detail-modal-footer">
    <button type="button" class="btn-modern-action btn-action-secondary"
      (click)="modal.dismiss('Close click')">
      <i class="bi bi-x-lg"></i> Close
    </button>
  </div>
</ng-template>

<!-- Create System Modal -->
<ng-template #createSystemModal let-modal>
  <div class="modal-header detail-modal-header">
    <div class="detail-modal-title-wrapper">
      <div class="detail-modal-icon">
        <i class="bi bi-plus-lg"></i>
      </div>
      <div>
        <h4 class="modal-title detail-modal-title" id="modal-basic-title">Create System</h4>
        <p class="detail-modal-subtitle">Add a new system definition</p>
      </div>
    </div>
    <button type="button" class="btn-close btn-close-white" aria-label="Close"
      (click)="modal.dismiss('Cross click')"></button>
  </div>
  <div class="modal-body form-modal-body">
    <form #systemForm="ngForm">
      <div class="form-field-group">
        <label for="systemName" class="form-modal-label">
          <i class="bi bi-tag-fill"></i> System Name <span class="required-star">*</span>
        </label>
        <input type="text" class="form-control form-modal-input" id="systemName"
          name="name" [(ngModel)]="modalContent!.name"
          placeholder="Enter system name" required />
      </div>
      <div class="form-field-group">
        <label for="systemMaterial" class="form-modal-label">
          <i class="bi bi-gem"></i> Material
        </label>
        <select class="form-select form-modal-input" id="systemMaterial"
          name="material" [(ngModel)]="modalContent!.material">
          <option value="">Select Material</option>
          <option value="Aluminum">Aluminum</option>
          <option value="Steel">Steel</option>
          <option value="Composite">Composite</option>
        </select>
      </div>
      <div class="form-field-group">
        <label for="systemType" class="form-modal-label">
          <i class="bi bi-layers-fill"></i> Type
        </label>
        <input type="text" class="form-control form-modal-input" id="systemType"
          name="type" [(ngModel)]="modalContent!.type"
          placeholder="Enter type" />
      </div>
    </form>
  </div>
  <div class="modal-footer detail-modal-footer">
    <button type="button" class="btn-modern-action btn-action-secondary"
      (click)="modal.dismiss('Cancel click')">
      <i class="bi bi-x-lg"></i> Cancel
    </button>
    <button type="button" class="btn-modern-action btn-action-primary"
      (click)="saveSystem(modalContent!)" [disabled]="!modalContent!.name">
      <i class="bi bi-check-lg"></i> Save System
    </button>
  </div>
</ng-template>

<!-- Edit System Modal -->
<ng-template #editSystemModal let-modal>
  <div class="modal-header detail-modal-header">
    <div class="detail-modal-title-wrapper">
      <div class="detail-modal-icon">
        <i class="bi bi-pencil-fill"></i>
      </div>
      <div>
        <h4 class="modal-title detail-modal-title" id="modal-basic-title">Edit System</h4>
        <p class="detail-modal-subtitle">{{ modalContent?.name }}</p>
      </div>
    </div>
    <button type="button" class="btn-close btn-close-white" aria-label="Close"
      (click)="modal.dismiss('Cross click')"></button>
  </div>
  <div class="modal-body form-modal-body">
    <form #systemForm="ngForm">
      <div class="form-field-group">
        <label for="editSystemName" class="form-modal-label">
          <i class="bi bi-tag-fill"></i> System Name <span class="required-star">*</span>
        </label>
        <input type="text" class="form-control form-modal-input" id="editSystemName"
          name="name" [(ngModel)]="modalContent!.name"
          placeholder="Enter system name" required />
      </div>
      <div class="form-field-group">
        <label for="editSystemMaterial" class="form-modal-label">
          <i class="bi bi-gem"></i> Material
        </label>
        <select class="form-select form-modal-input" id="editSystemMaterial"
          name="material" [(ngModel)]="modalContent!.material">
          <option value="">Select Material</option>
          <option value="Aluminum">Aluminum</option>
          <option value="Steel">Steel</option>
          <option value="Composite">Composite</option>
        </select>
      </div>
      <div class="form-field-group">
        <label for="editSystemType" class="form-modal-label">
          <i class="bi bi-layers-fill"></i> Type
        </label>
        <input type="text" class="form-control form-modal-input" id="editSystemType"
          name="type" [(ngModel)]="modalContent!.type"
          placeholder="Enter type" />
      </div>
    </form>
  </div>
  <div class="modal-footer detail-modal-footer">
    <button type="button" class="btn-modern-action btn-action-secondary"
      (click)="modal.dismiss('Cancel click')">
      <i class="bi bi-x-lg"></i> Cancel
    </button>
    <button type="button" class="btn-modern-action btn-action-primary"
      (click)="saveSystem(modalContent!)" [disabled]="!modalContent!.name">
      <i class="bi bi-check-lg"></i> Update System
    </button>
  </div>
